/**
 * The photo points of an acquisition, read from its `photos.gpkg` in the browser.
 *
 * A GeoPackage is a SQLite file; sql.js opens it in WebAssembly. The `photos` table carries
 * `lat`/`lon` beside the geometry, so no WKB decoding is needed. `exif_json` and `xmp_json`
 * are never selected: they are most of the file and nothing on the map reads them.
 */
import type { FeatureCollection, Point } from 'geojson';
import { GpkgError, openGpkg } from './gpkg';
import type { PositionSource } from './photoPositioning';

export interface PhotoProperties {
  path: string;
  name: string;
  flight: string | null;
  time: string | null;
  rel_altitude: number | null;
  abs_altitude: number | null;
  agl_m: number | null;
  gimbal_yaw: number | null;
  gimbal_pitch: number | null;
  tilt_deg: number | null;
  speed: number | null;
  gps_status: string | null;
  rtk_flag: number | null;
  geo_error_h_m: number | null;
  geo_error_v_m: number | null;
  width: number | null;
  height: number | null;
  size_bytes: number | null;
  model: string | null;
  drone_model: string | null;
  forward_overlap_pct: number | null;
  side_overlap_pct: number | null;
  /** RTKLIB's quality of the photo's PPK solution (1 fixed, 2 float); absent from older files. */
  ppk_quality?: number | null;
  /** The offset an anchored flight's photo took off its EXIF altitude; absent from older files. */
  anchor_offset_m?: number | null;
  /** Where the camera position comes from, once the acquisition's positioning is known (`photoPositioning.ts`). */
  position_source?: PositionSource | null;
  position_acc_h_m?: number | null;
  position_acc_v_m?: number | null;
}

export const PHOTO_COLUMNS = [
  'path', 'name', 'flight', 'time', 'rel_altitude', 'abs_altitude', 'agl_m', 'gimbal_yaw', 'gimbal_pitch',
  'tilt_deg', 'speed', 'gps_status', 'rtk_flag', 'geo_error_h_m', 'geo_error_v_m', 'width', 'height',
  'size_bytes', 'model', 'drone_model', 'forward_overlap_pct', 'side_overlap_pct',
] as const;

/** Columns a newer preprocess writes (forestum image 1.4 and later): read when the file has them. */
export const OPTIONAL_PHOTO_COLUMNS = ['ppk_quality', 'anchor_offset_m'] as const;

export type PhotoSourceErrorCode = 'fetch' | 'not_gpkg' | 'no_photos_table';

export class PhotoSourceError extends Error {
  constructor(
    public readonly code: PhotoSourceErrorCode,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'PhotoSourceError';
  }
}

export async function loadPhotoPoints(url: string, fetchImpl: typeof fetch = fetch): Promise<FeatureCollection<Point, PhotoProperties>> {
  const response = await fetchImpl(url);
  if (!response.ok) throw new PhotoSourceError('fetch', `bucket ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let db;
  try {
    db = await openGpkg(bytes);
  } catch (error) {
    if (error instanceof GpkgError && error.code === 'not_gpkg') throw new PhotoSourceError('not_gpkg');
    throw error;
  }
  try {
    let rows: unknown[][];
    let names: string[];
    try {
      const present = new Set((db.exec('PRAGMA table_info(photos)')[0]?.values ?? []).map((r) => String(r[1])));
      const optional = OPTIONAL_PHOTO_COLUMNS.filter((c) => present.has(c));
      const columns = ['lon', 'lat', ...PHOTO_COLUMNS, ...optional].join(', ');
      const result = db.exec(`SELECT ${columns} FROM photos ORDER BY time, path`)[0];
      rows = result?.values ?? [];
      names = result?.columns ?? [];
    } catch (error) {
      // Only a missing table is a data problem; a column mismatch or other SQL error is a defect
      // and must surface as such rather than be swallowed into `no_photos_table`.
      if (error instanceof Error && /no such table/i.test(error.message)) throw new PhotoSourceError('no_photos_table');
      throw error;
    }
    const features = rows.flatMap((row) => {
      const record = Object.fromEntries(names.map((n, i) => [n, row[i]])) as Record<string, unknown>;
      const { lon, lat } = record;
      // `typeof` (not `Number(...)`) matters here: `Number(null)` is `0`, a finite number, which
      // would let a row with a genuinely missing coordinate through as if it were on the equator.
      if (typeof lon !== 'number' || !Number.isFinite(lon) || typeof lat !== 'number' || !Number.isFinite(lat) || typeof record.path !== 'string') return [];
      const properties = Object.fromEntries([...PHOTO_COLUMNS, ...OPTIONAL_PHOTO_COLUMNS].map((c) => [c, record[c] ?? null])) as unknown as PhotoProperties;
      return [{ type: 'Feature' as const, id: record.path as string, geometry: { type: 'Point' as const, coordinates: [lon, lat] }, properties }];
    });
    return { type: 'FeatureCollection', features };
  } finally {
    db.close();
  }
}
